import { setupRequest } from './accountSetupService';

export type SupplierSetupJobKind = 'analyze' | 'process' | 'recheck';
export type SupplierSectionType = 'FOOD' | 'DRINKS' | 'NON_FOOD';
export interface SupplierSetupSection { id: string; name: string; type: SupplierSectionType; }
export interface SupplierSetupConfig { id: string; sectionId: string; clientNames: string[]; orderEmails: string[]; combinedOrderingAccount?: boolean; }
export interface SupplierSetupFile {
  id: string;
  filename: string;
  clientNames: string[];
  suggestedSectionType: SupplierSectionType;
  analysisStatus: 'pending' | 'done' | 'failed';
  processingStatus: 'pending' | 'verified' | 'needs_attention';
  quantityFlagCount: number | null;
  hasQuantityFlags?: boolean;
  totalsStatus: 'passed' | 'failed' | 'inconclusive' | null;
  issues: string[];
  orders: { id: string; orderNumber: string }[];
  /** Document layout for suppliers with several layouts (see SupplierSetupGroup.variants); null = unknown. */
  supplierVariant?: string | null;
  /** Set when an admin accepted a false-positive check; cleared by any new automatic result. */
  manuallyVerified?: { at: string };
}
export interface SupplierSetupGroup {
  platform: string;
  fileCount: number;
  orderEmailSuggestion: string | null;
  configs: SupplierSetupConfig[];
  files: SupplierSetupFile[];
  job: { kind: SupplierSetupJobKind; status: 'running' | 'complete' | 'failed'; error?: string; totalFiles?: number; completedFiles?: number; currentFileId?: string | null } | null;
  supported: boolean;
  /** Selectable document layouts; empty for single-layout suppliers. */
  variants?: string[];
}
export interface SupplierSetupOverview { sections: SupplierSetupSection[]; suppliers: SupplierSetupGroup[]; }
export interface SupplierSetupConfiguration {
  clientNames: string[];
  sectionId?: string;
  sectionType?: SupplierSectionType;
  orderEmails: string[];
  combinedOrderingAccount?: boolean;
}
const base = (restaurantId: string) => `/api/admin/account-setup/restaurants/${encodeURIComponent(restaurantId)}`;
const supplierPath = (restaurantId: string, platform: string) => `${base(restaurantId)}/suppliers/${encodeURIComponent(platform)}`;
export const getSetupSuppliers = (restaurantId: string) => setupRequest<SupplierSetupOverview>(`${base(restaurantId)}/suppliers`);
export const startSetupSupplierJob = (restaurantId: string, platform: string, kind: SupplierSetupJobKind, fileId?: string) =>
  setupRequest<{ started: boolean }>(`${supplierPath(restaurantId, platform)}/${kind}`, { method: 'POST', ...(fileId ? { body: JSON.stringify({ fileId }) } : {}) });
export const configureSetupSupplier = (restaurantId: string, platform: string, config: SupplierSetupConfiguration) =>
  setupRequest<{ platformId: string; sectionId: string }>(`${supplierPath(restaurantId, platform)}/configure`, { method: 'POST', body: JSON.stringify(config) });
export const setSetupSupplierFileVariant = (restaurantId: string, platform: string, fileId: string, variant: string) =>
  setupRequest<{ updated: boolean }>(`${supplierPath(restaurantId, platform)}/files/${encodeURIComponent(fileId)}/variant`, { method: 'POST', body: JSON.stringify({ variant }) });
export const markSetupSupplierFileVerified = (restaurantId: string, platform: string, fileId: string) =>
  setupRequest<{ verified: boolean }>(`${supplierPath(restaurantId, platform)}/files/${encodeURIComponent(fileId)}/verify`, { method: 'POST' });
/** Deletes the orders/invoices this file saved and resets it to pending, so it can be processed again. */
export const resetSetupSupplierFile = (restaurantId: string, platform: string, fileId: string) =>
  setupRequest<{ deletedOrders: number; deletedInvoices: number; deletedPriceAlerts: number }>(`${supplierPath(restaurantId, platform)}/files/${encodeURIComponent(fileId)}/reset`, { method: 'POST' });
export const previewSetupSupplierFile = (restaurantId: string, fileId: string) =>
  setupRequest<{ url: string }>(`${base(restaurantId)}/steps/supplier_invoices/files/${encodeURIComponent(fileId)}/preview`);

/** Keep the supplier extractor's spelling; only collapse case/whitespace equivalents. */
export function supplierClientGroups(supplier: SupplierSetupGroup) {
  const groups = new Map<string, { name: string; files: SupplierSetupFile[]; suggestedSectionType: SupplierSectionType }>();
  for (const file of supplier.files) {
    for (const rawName of file.clientNames) {
      const name = rawName.trim();
      if (!name) continue;
      const key = name.normalize('NFC').toLowerCase();
      const group = groups.get(key);
      if (group) {
        if (!group.files.some(existing => existing.id === file.id)) group.files.push(file);
      } else groups.set(key, { name, files: [file], suggestedSectionType: file.suggestedSectionType });
    }
  }
  return Array.from(groups.values());
}
export function supplierNameConfig(supplier: SupplierSetupGroup, name: string) {
  return supplier.configs.find(config => config.clientNames.some(value => value.trim().normalize('NFC').toLowerCase() === name.trim().normalize('NFC').toLowerCase()));
}
/** Multi-layout suppliers never process a file whose layout is unknown. */
export function supplierFileNeedsVariant(supplier: SupplierSetupGroup, file: SupplierSetupFile) {
  return !!supplier.variants?.length && !supplier.variants.includes(file.supplierVariant ?? '');
}
export function supplierFileReadyToProcess(supplier: SupplierSetupGroup, file: SupplierSetupFile) {
  return supplier.supported && supplier.job?.status !== 'running' && file.processingStatus !== 'verified' && !supplierFileNeedsVariant(supplier, file)
    && file.analysisStatus === 'done' && file.clientNames.some(name => name.trim())
    && file.clientNames.filter(name => name.trim()).every(name => !!supplierNameConfig(supplier, name));
}
export function supplierReadyToProcess(supplier: SupplierSetupGroup) {
  const remaining = supplier.files.filter(file => file.processingStatus !== 'verified');
  return remaining.length > 0 && remaining.every(file => supplierFileReadyToProcess(supplier, file));
}
